import { type FC } from 'react';
import classnames from 'classnames';
import { graphql, useFragment, usePreloadedQuery, type PreloadedQuery } from 'react-relay';

import { Link } from 'dibs-elements/exports/Link.tsx';
import { FormattedMessage } from 'dibs-react-intl';
import { Spinner } from 'dibs-elements/exports/Spinner.tsx';

import { userNotificationsQuery } from './UserNotifications.tsx';

import { useGetAllUnreadNotifications } from './useGetAllUnreadNotifications.ts';
import { useNotificationsDeleteTags } from './useNotificationsDeleteTags.ts';
import { trackUserNotifications } from './userNotificationsTracking.ts';

import { type UserNotificationsQuery } from './__generated__/UserNotificationsQuery.graphql.ts';
import { type UserNotificationsMarkAllAsRead_viewer$key } from './__generated__/UserNotificationsMarkAllAsRead_viewer.graphql.ts';

import dibsCss from 'dibs-css';

const viewerFragment = graphql`
    fragment UserNotificationsMarkAllAsRead_viewer on Viewer {
        userNotifications(userId: $userId) {
            ...useGetAllUnreadNotifications_userNotifications
        }
    }
`;

const UserNotificationsMarkAllAsRead: FC<{
    queryRef: PreloadedQuery<UserNotificationsQuery>;
    userId: string;
    hasTradeUserId: boolean;
}> = ({ queryRef, userId, hasTradeUserId }) => {
    const data = usePreloadedQuery<UserNotificationsQuery>(userNotificationsQuery, queryRef);
    const viewer = useFragment<UserNotificationsMarkAllAsRead_viewer$key>(
        viewerFragment,
        data.viewer
    );
    const { userNotifications } = viewer || {};

    const { unreadNotificationsCount, unreadNotificationsIds } = useGetAllUnreadNotifications({
        userNotifications: userNotifications ?? null,
    });

    const { deleteTagFromAllNotifications, deleteTagFromAllNotificationsInFlight } =
        useNotificationsDeleteTags();

    if (unreadNotificationsCount <= 0) {
        return null;
    }

    return (
        <span className={classnames(dibsCss.flex, dibsCss.itemsCenter, dibsCss.gapXxsmall)}>
            <Link
                onClick={() => {
                    deleteTagFromAllNotifications({
                        ...unreadNotificationsIds,
                        hasTradeUserId,
                        userId,
                        tag: 'UNREAD',
                    });

                    trackUserNotifications({
                        stepInteractionName: 'mark all as read clicked',
                    });
                }}
            >
                <FormattedMessage
                    id="dbl.UserNotificationsMarkAllAsRead.link"
                    defaultMessage="Mark All As Read"
                />
            </Link>
            {deleteTagFromAllNotificationsInFlight && <Spinner size="tiny" />}
        </span>
    );
};

export { UserNotificationsMarkAllAsRead };
